import { useQuery } from "@apollo/client";
import a from "indefinite";
import { uniqBy } from "lodash";
import { ReactElement, useMemo, useState } from "react";
import {
  GetAllOrganizationTeamsQueryQuery,
  GetAllOrganizationTeamsQueryQueryVariables,
} from "types/graphql-schema";

import getAllOrganizationTeamsQuery from "@apps/artifact/graphql/get-all-organization-teams-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import { ButtonSize } from "@components/button/button-constants";
import ComboboxGeneric, {
  ComboboxGenericButtonProps,
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

export type TeamPickerTeam = {
  id: number;
  title: string;
};

export const TeamPickerDropdown = ({
  teams,
  onChange,
  createLabel,
  onClickCreateNew,
  className,
  children,
}: {
  teams: TeamPickerTeam[];
  onChange: (value: TeamPickerTeam) => void;
  // Optional "Create new…" item shown at the top of the menu.
  createLabel?: string;
  onClickCreateNew?: () => void;
  // Sizes the wrapper the button sits in, for a picker used as a form field.
  className?: string;
  children: ReactElement<ComboboxGenericButtonProps>;
}) => {
  const currentOrganization = currentOrganizationVar();
  const teamIds = teams.map(({ id }) => id);
  const [canLoadData, setCanLoadData] = useState(false);

  // Read from the query's data rather than copying it once, so a team
  // created from the picker shows up when the list is refetched.
  const { data, loading } = useQuery<
    GetAllOrganizationTeamsQueryQuery,
    GetAllOrganizationTeamsQueryQueryVariables
  >(getAllOrganizationTeamsQuery, {
    variables: {
      organizationId: currentOrganization.id,
    },
    skip: !canLoadData,
  });
  const options: ComboboxGenericOption<TeamPickerTeam>[] = useMemo(
    () =>
      data
        ? uniqBy(assertEdgesNonNull(data.organization?.teams), "id").map(
            (team) => ({ label: team.title, value: team }),
          )
        : [],
    [data],
  );

  const filteredOptions = options.filter(
    (option) => !option.value.id || !teamIds.includes(option.value.id),
  );

  const handleChangeOwner = (option: ComboboxGenericOption<TeamPickerTeam>) => {
    onChange(option.value);
  };

  const handlePreloadData = () => {
    setCanLoadData(true);
  };

  return (
    <ComboboxGeneric
      // eslint-disable-next-line local-rules/use-label
      aria-label="Team picker dropdown"
      className={className}
      loading={loading}
      value={null}
      options={filteredOptions}
      onChangeValue={handleChangeOwner}
      onClickButton={handlePreloadData}
      createLabel={onClickCreateNew ? createLabel : undefined}
      onClickCreateNew={onClickCreateNew}
    >
      {children}
    </ComboboxGeneric>
  );
};

const TeamPicker = ({
  teams,
  onRemoveTeam,
  onAddTeam,
  canChange,
  onClickCreateNew,
  size = ButtonSize.large,
}: {
  teams: TeamPickerTeam[];
  onRemoveTeam: (recipient: TeamPickerTeam) => void;
  onAddTeam: (option: TeamPickerTeam) => void;
  canChange: boolean;
  onClickCreateNew?: () => void;
  size?: ButtonSize;
}) => {
  const label = useLabel();
  return (
    <div className="flex-1 flex gap-2 flex-wrap">
      {teams.map((team) => (
        <div
          // Same height as the picker button beside it, on a grey fill so the
          // chips read as values rather than more buttons.
          className={classNames(
            "rounded-lg px-2 text-sm flex items-center gap-1 bg-gray-100",
            size === ButtonSize.small
              ? "h-[20px]"
              : size === ButtonSize.large
                ? "h-[32px]"
                : "h-[24px]",
          )}
          key={team.id}
        >
          {team.title}
          {canChange && (
            <button
              className="ml-0.5 rounded-full p-0.5 border bg-gray-50 text-gray-600 hover:text-white hover:bg-blue-600 hover:border-blue-600"
              onClick={() => onRemoveTeam(team)}
            >
              <TopicflowIcon type="close" className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
      {canChange && (
        <TeamPickerDropdown
          teams={teams}
          onChange={onAddTeam}
          createLabel={`Create new ${label("team")}`}
          onClickCreateNew={onClickCreateNew}
        >
          <ComboboxGeneric.Button size={size}>
            <TopicflowIcon type="plus" className="h-4 w-4" />
            Pick {a(label("team"))}
          </ComboboxGeneric.Button>
        </TeamPickerDropdown>
      )}
    </div>
  );
};

export default TeamPicker;
